import { useContext, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { Tooltip as ReactTooltip } from "react-tooltip";
import ApplicationContext from "../context/application/ApplicationContext";
import TenantContext from "../context/tenant/TenantContext";
import AuthContext from "../context/auth/AuthContext";
import useTranslator from "../hooks/useTranslator";
import SkorDefaultLoader from "../assets/img/skorDefaultLoader.gif";
import { haveValue, isValidEmail, toast } from "../helper/common";
import {
  getInitials,
  resolveParticipantName,
} from "../helper/applicationHelpers";
import UserIcon from "../assets/img/user-icon.svg";
import SkorProtectSelector from "./SkorProtectSelector/SkorProtectSelector";
import SkorProtectBadge from "./SkorProtectBadge/SkorProtectBadge";
import CreateGroupScreen from "./CreateGroupScreen/CreateGroupScreen";
import ClientIdentificationForm from "./ClientIdentificationForm/ClientIdentificationForm";
import constants from "../Utils/constants";

/* ── small avatar circle ── */
const Avatar = ({ name, size = 40, bg = "#a3eb64", color = "#1E2528" }) => (
  <div
    style={{
      width: size,
      height: size,
      minWidth: size,
      borderRadius: "50%",
      background: bg,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 700,
      fontSize: size * 0.35,
      color,
      textTransform: "uppercase",
    }}
  >
    {getInitials(name)}
  </div>
);

const resolveCreatorName = (created_by) => {
  if (!created_by || typeof created_by === "string") return "";
  const fname = created_by.fname || created_by.firstname || "";
  const lname = created_by.lname || created_by.lastname || "";
  return `${fname} ${lname}`.trim() || created_by.email || "";
};

const resolveCreatorEmail = (created_by) => {
  if (!created_by || typeof created_by === "string") return null;
  return created_by.email ?? null;
};

const resolveGuarantorName = (entry) => {
  if (!entry || typeof entry === "string") return "";
  const guarantor =
    entry.guarantor_id ?? entry.gurantor_id ?? entry.guarantor ?? entry;
  const name =
    entry.gurantor_name ??
    entry.guarantor_name ??
    `${guarantor?.fname ?? guarantor?.firstname ?? ""} ${
      guarantor?.lname ?? guarantor?.lastname ?? ""
    }`.trim();
  return (
    name ||
    entry.gurantor_email ||
    entry.guarantor_email ||
    guarantor?.email ||
    ""
  );
};

const getParticipantGuarantors = (participant, user, isMe) => {
  const candidates = [
    participant?.id?.gurantor_id,
    participant?.tenant_info?.gurantor_id,
    isMe ? user?.gurantor_added_id : null,
    participant?.guarantors,
    participant?.guarantorIds,
  ];
  for (const candidate of candidates) {
    if (!Array.isArray(candidate)) continue;
    const guarantors = candidate
      .map((entry) => ({ entry, name: resolveGuarantorName(entry) }))
      .filter(({ name }) => name);
    if (guarantors.length > 0) return guarantors;
  }
  return [];
};

const isSharedWithLandlord = (app, landlordId) => {
  if (!landlordId) return false;
  const extracted_landlord_id = landlordId?.split?.("_")?.[0] ?? landlordId;
  const inShareRequests = (app.shareRequests ?? []).some(
    (entry) => entry?.landlord_id?._id === extracted_landlord_id
  );
  if (inShareRequests) return true;
  const inSharedProfiles = (app.sharedProfiles ?? []).some(
    (sp) => sp?.id?.landlordid?._id === extracted_landlord_id
  );
  return inSharedProfiles;
};

/* ── SCREEN 1 – choice ── */
const ChoiceScreen = ({
  landlordData,
  applicationToShare,
  applicationOnly,
  hideIndividualOption,
  onSelectIndividual,
  onSelectApplication,
  onSelectCreate,
  onClose,
  processing,
  individualSharing,
  _t,
  user,
}) => {
  const landlordName =
    `${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() ||
    landlordData?.email ||
    _t("landlord", "Landlord");
  const individualProfileTitle =
    typeof user?.application_title === "string" && user.application_title.trim()
      ? user.application_title.trim()
      : _t("myIndividualProfile", "My individual profile");

  return (
    <div className="isf-modal">
      {/* Dark header */}
      <div className="isf-header">
        <button
          className="isf-close-btn"
          onClick={onClose}
          disabled={processing}
          aria-label="close"
        >
          {processing ? "..." : "✕"}
        </button>
        <div className="isf-header-landlord">
          <Avatar name={landlordName} size={44} bg="#a3eb64" color="#1E2528" />
          <div className="isf-header-landlord-info">
            <span className="isf-header-landlord-name">{landlordName}</span>
            <span className="isf-header-landlord-sub">
              {_t(
                "hasInvitedYouToShareYourProfile",
                "has invited you to share your profile"
              )}
            </span>
          </div>
        </div>
        <h2 className="isf-header-title">
          {_t(
            "whatWouldYouLikeToShare",
            "What would you like to share with this landlord?"
          )}
        </h2>
      </div>

      {/* Options */}
      <div className="isf-options">
        {/* Individual profile */}
        {!applicationOnly && !hideIndividualOption && (
          <button
            className={`isf-option-card${individualSharing ? " isf-option-card-sharing" : ""}`}
            onClick={onSelectIndividual}
            disabled={individualSharing}
            aria-busy={individualSharing}
          >
            <div className="isf-option-icon isf-option-icon-person">
              <img src={UserIcon} alt="User" />
            </div>
            <div className="isf-option-text">
              <span className="isf-option-title">
                {individualSharing
                  ? _t("sharing", "Sharing...")
                  : individualProfileTitle}
              </span>
              <span className="isf-option-sub">
                {_t("shareOnlyYourOwnProfile", "Share only your own profile.")}
              </span>
            </div>
            <svg
              className="isf-chevron"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        )}

        {/* Latest application */}
        {(applicationToShare ? [applicationToShare] : []).map((app) => {
          const participants = app.participants ?? [];
          return (
            <div
              key={app._id}
              className={`isf-option-card isf-option-card-group${processing ? " isf-option-card-sharing" : ""}`}
              aria-disabled={processing}
            >
              <div
                className="isf-option-card-inner"
                style={processing ? { pointerEvents: "none" } : undefined}
                onClick={() => !processing && onSelectApplication(app)}
              >
                <div className="isf-option-icon isf-option-icon-group">
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <div className="isf-option-text">
                  <span className="isf-option-title">{app.title}</span>
                  <div className="isf-chips-row">
                    {participants.map((p, i) => {
                      const name = resolveParticipantName(p);
                      const isMe =
                        p.email?.toLowerCase() === user?.email?.toLowerCase();
                      const guarantors = getParticipantGuarantors(
                        p,
                        user,
                        isMe
                      );
                      return (
                        <div
                          key={i}
                          className="isf-chip-stack"
                          aria-label={
                            guarantors.length > 0
                              ? `${isMe ? _t("you", "You") : name}, ${guarantors.length} ${_t("guarantor", "guarantor")}`
                              : isMe
                                ? _t("you", "You")
                                : name
                          }
                        >
                          <div
                            className={`isf-chip${isMe ? " isf-chip-me" : ""}`}
                            title={name}
                          >
                            {isMe ? _t("you", "You") : getInitials(name)}
                          </div>
                          {guarantors.slice(0, 3).map(({ entry, name }, j) => (
                            <div
                              key={
                                entry?._id ??
                                entry?.guarantor_id?._id ??
                                entry?.gurantor_id?._id ??
                                `${name}-${j}`
                              }
                              className="isf-chip isf-chip-guarantor"
                              title={`${name} · ${_t("guarantor", "Guarantor")}`}
                            >
                              {getInitials(name)}
                            </div>
                          ))}
                          {guarantors.length > 3 && (
                            <div
                              className="isf-chip isf-chip-guarantor isf-chip-overflow"
                              title={`${guarantors.length - 3} ${_t("more", "more")}`}
                            >
                              +{guarantors.length - 3}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <svg
                  className="isf-chevron"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </div>
            </div>
          );
        })}

        {/* Create new group */}
        <button
          className="isf-option-card isf-option-card-create"
          onClick={onSelectCreate}
          disabled={processing}
        >
          <div className="isf-option-icon isf-option-icon-create">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 5v14M5 12h14" />
            </svg>
          </div>
          <div className="isf-option-text">
            <span className="isf-option-title">
              {_t("createANewGroup", "Create a new group")}
            </span>
            <span className="isf-option-sub">
              {_t(
                "inviteYourFlatmatesByEmail",
                "Invite your flatmates by email."
              )}
            </span>
          </div>
          <svg
            className="isf-chevron"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/* ── SCREEN 1b – individual profile + skor protect ── */
const IndividualScreen = ({
  landlordData,
  onBack,
  onConfirm,
  processing,
  _t,
}) => {
  const [skorProtect, setSkorProtect] = useState(true);
  const landlordName =
    `${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() ||
    landlordData?.email ||
    _t("landlord");

  return (
    <div className="isf-modal isf-modal-sub">
      <div className="isf-sub-header">
        <button className="isf-back-btn" onClick={onBack}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          {_t("back")}
        </button>
        <div className="isf-sub-title-block">
          <h2 className="isf-sub-title">{_t("shareYourIndividualProfile")}</h2>
          <span className="isf-sub-landlord">
            {_t("with")} {landlordName}
          </span>
        </div>
      </div>

      <div className="isf-body">
        <SkorProtectSelector value={skorProtect} onChange={setSkorProtect} />
      </div>

      <div className="isf-footer">
        <button
          className="isf-btn isf-btn-outline"
          onClick={onBack}
          disabled={processing}
        >
          {_t("back")}
        </button>
        <button
          className="isf-btn isf-btn-primary"
          onClick={() => onConfirm(skorProtect)}
          disabled={processing}
        >
          {processing ? "..." : _t("shareProfile")}
        </button>
      </div>
    </div>
  );
};

/* ── SCREEN 2 – join group ── */
export { Avatar, resolveCreatorEmail };
const JoinGroupScreen = ({
  app,
  landlordData,
  onBack,
  onClose,
  onAccept,
  onDecline,
  processing,
  user,
  _t,
}) => {
  const creatorName = resolveCreatorName(app.created_by);
  const creatorEmail = resolveCreatorEmail(app.created_by);
  const participants = app.participants ?? [];
  const landlordName =
    `${landlordData?.fname_companyName ?? ""} ${landlordData?.lname ?? ""}`.trim() ||
    _t("theLandlord", "the landlord");

  const getRole = (p) => {
    const isOwner =
      creatorEmail && p.email?.toLowerCase() === creatorEmail.toLowerCase();
    if (isOwner) return "owner";
    const isMe = p.email?.toLowerCase() === user?.email?.toLowerCase();
    if (isMe) return "pending";
    return p.consent_status ?? "pending";
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case "owner":
        return _t("groupOwner", "Group owner");
      case "pending":
        return _t("pendingAcceptance", "Pending acceptance");
      case "accepted":
        return _t("accepted", "Accepted");
      case "rejected":
        return _t("rejected", "Rejected");
      default:
        return role;
    }
  };

  return (
    <div className="isf-modal isf-modal-sub">
      {/* Sub-screen header */}
      <div className="isf-sub-header">
        {onBack && (
          <button className="isf-back-btn" onClick={onBack}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
            {_t("back", "Back")}
          </button>
        )}
        <div className="isf-sub-title-block">
          <h2 className="isf-sub-title">
            {`${_t("join")} ${creatorName}'s ${_t("group")}`}
          </h2>
          {landlordData && (
            <span className="isf-sub-landlord">
              {_t("with", "with")} {landlordName}
            </span>
          )}
        </div>
      </div>

      {/* Members section */}
      <div className="isf-body">
        <div className="isf-members-card">
          <div className="isf-members-card-header">
            <span className="isf-members-label">
              {_t("groupMembers", "Group members")}
            </span>
            {app.skor_protect && (
              <>
                <span
                  data-tooltip-id="isf-sp-badge-tooltip"
                  className="d-flex align-items-center"
                >
                  <SkorProtectBadge />
                </span>
                <ReactTooltip
                  id="isf-sp-badge-tooltip"
                  place="bottom"
                  className="isf-sp-tooltip-dark"
                  render={() => (
                    <span className="isf-sp-tooltip-text">
                      {participants?.[0]?.name +
                        " " +
                        _t("skorProtectGroupTooltip")}
                    </span>
                  )}
                />
              </>
            )}
          </div>
          <div className="isf-members-list">
            {participants.map((p, i) => {
              const name = resolveParticipantName(p);
              const isMe =
                p.email?.toLowerCase() === user?.email?.toLowerCase();
              const role = getRole(p);
              return (
                <div key={i} className="isf-member-row">
                  <Avatar
                    name={isMe ? "You" : name}
                    size={40}
                    bg={role === "owner" ? "#a3eb64" : "#d8d8d8"}
                    color={role === "owner" ? "#1E2528" : "#555"}
                  />
                  <div className="isf-member-info">
                    <span className="isf-member-name">
                      {isMe ? _t("you", "You") : name}
                    </span>
                    <span className="isf-member-role">
                      {getRoleLabel(role)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <p className="isf-join-disclaimer">
          {landlordData
            ? _t("byAcceptingYourProfileWillBeShared")
            : _t("byAcceptingYouWillJoinGroup")}
        </p>
      </div>

      {/* Footer */}
      <div className="isf-footer">
        <button
          className="isf-btn isf-btn-outline"
          onClick={onDecline}
          disabled={processing}
        >
          {_t("decline", "Decline")}
        </button>
        <button
          className="isf-btn isf-btn-primary"
          onClick={onAccept}
          disabled={processing}
        >
          {processing ? "..." : _t("acceptAndJoin", "Accept & join")}
        </button>
      </div>
    </div>
  );
};

/* ── MAIN COMPONENT ── */
const InvitationShareModal = ({
  isOpen,
  landlordId, // cookies.requestProfileLink — may become undefined after cookies clear
  propertyId, // cookies.property_id
  onClose,
  onClearCookies,
  onNeedsConsent,
  onDone,
  // Website-entry reuse (cases 3-4): drive the same modal from a landlord
  // request loaded via API instead of a landlord link. When requestOverride is
  // provided, the link-registration flow is skipped.
  requestOverride,
  applicationOverride,
  closeBehavior,
  onCreateNew,
  landlordDataOverride,
  compactChoice,
  // Onboarding-only gate: when true (and driven by requestOverride), every
  // share action runs the Vivia check for the selected application first and
  // opens ClientIdentificationForm before acceptLandlordRequest.
  enableViviaGate = false,
  // Called when the gated identification form is submitted (POST succeeded).
  // Lets the parent refresh the tenant after the share completes.
  onViviaSubmitted,
}) => {
  const { _t } = useTranslator();
  const { user } = useContext(AuthContext);
  const { getLandlordsConsentInfo, loadSharedProfiles } =
    useContext(TenantContext);
  const {
    pendingApplications,
    myApplications,
    createApplication,
    consentApplication,
    loadApplicationsData,
    openLandlordLink,
    loadLandlordRequests,
    acceptLandlordRequest,
  } = useContext(ApplicationContext);

  const currentLang = localStorage.getItem("i18nextLng").toLowerCase() || "en";

  const defaultTitle =
    currentLang === "es"
      ? `Aplicación de ${user?.fname}`
      : `${user?.fname}'s Application`;

  const [step, setStep] = useState("choice");
  const [selectedApp, setSelectedApp] = useState(null);
  const [landlordData, setLandlordData] = useState(null);
  const [landLordLoading, setLandlordLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [individualSharing, setIndividualSharing] = useState(false);
  // Deferred share waiting on the Vivia identification form. Stays set while
  // the form is open; cancel blocks the share, submit runs action.
  const [pendingVivia, setPendingVivia] = useState(null);
  // Snapshot IDs so parent cookie-clearing doesn't affect in-flight actions
  const capturedLandlordId = useRef(null);
  const requestId = useRef(null);
  const fetchedRef = useRef(false);
  // true when user arrived via the signup flow (tenantSignupFlow cookie was set)
  const isSignupFlowRef = useRef(false);
  const [signupCookies, , removeSignupCookie] = useCookies([
    "tenantSignupFlow",
  ]);

  useEffect(() => {
    if (isOpen && requestOverride && !fetchedRef.current) {
      // API-driven mode: request already exists, no link registration needed.
      fetchedRef.current = true;
      requestId.current = requestOverride._id ?? null;
      const landlord = requestOverride.landlord_id ?? {};
      const openedWorkspaceRef = requestOverride.workspace_id;
      console.log("[signup-flow]", "landlord share modal opened (API mode)", {
        requestId: requestOverride._id ?? null,
        landlord:
          `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
          landlord.email ||
          null,
        hasApplication: Boolean(applicationOverride?._id),
        closeBehavior: closeBehavior ?? "share",
        clientIdentificationRule: {
          request: requestOverride?.client_identification_rule === true,
          landlord: landlord?.client_identification_rule === true,
          workspace:
            openedWorkspaceRef && typeof openedWorkspaceRef === "object"
              ? openedWorkspaceRef?.client_identification_rule === true
              : null,
        },
      });
      setLandlordData(
        landlordDataOverride ?? {
          fname_companyName: landlord.fname_companyName,
          lname: landlord.lname,
          email: landlord.email,
          insurance_service_status: landlord.insurance_service_status,
          affidavit_activated: landlord.affidavit_activated,
          client_identification_rule:
            landlord.client_identification_rule ??
            requestOverride?.client_identification_rule ??
            null,
        }
      );
      setLandlordLoading(false);
      return;
    }
    if (isOpen && landlordId && !fetchedRef.current) {
      fetchedRef.current = true;
      // Snapshot before cookies are cleared
      capturedLandlordId.current = landlordId;
      const capturedPropertyId = propertyId;
      // Capture signup-flow flag before clearing cookies
      isSignupFlowRef.current = haveValue(signupCookies.tenantSignupFlow);

      setLandlordLoading(true);

      Promise.all([
        getLandlordsConsentInfo(landlordId),
        openLandlordLink(landlordId, capturedPropertyId),
        loadApplicationsData(),
      ])
        .then(([consentRes, linkRes]) => {
          setLandlordData(consentRes?.data ?? consentRes);
          // Store the request ID returned by the API
          requestId.current =
            linkRes?.data?._id ??
            linkRes?.success_res?._id ??
            linkRes?._id ??
            null;
          // Clear cookies now that the link has been registered
          onClearCookies();
          removeSignupCookie("tenantSignupFlow", { path: "/" });
          // Refresh landlord requests to surface any notifications
          return loadLandlordRequests();
        })
        .catch(() => {
          // Still clear cookies on failure to avoid re-triggering
          onClearCookies();
          removeSignupCookie("tenantSignupFlow", { path: "/" });
        })
        .finally(() => setLandlordLoading(false));
    }
    if (!isOpen) {
      fetchedRef.current = false;
      setStep("choice");
      setSelectedApp(null);
      setIndividualSharing(false);
      setPendingVivia(null);
      requestId.current = null;
      capturedLandlordId.current = null;
      isSignupFlowRef.current = false;
    }
  }, [isOpen, landlordId]);

  // Signup flow: filter to apps already shared with this landlord (flatmate added you).
  // Already-logged-in flow: skip the landlord filter, just surface the latest pending app.
  const pendingAppsForLandlord = isSignupFlowRef.current
    ? (() => {
        const filtered = (pendingApplications ?? []).filter((app) =>
          isSharedWithLandlord(app, capturedLandlordId.current ?? landlordId)
        );

        filtered.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );

        return filtered.slice(0, 1);
      })()
    : (() => {
        const sorted = [...(pendingApplications ?? [])].sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        return sorted.slice(0, 1);
      })();

  const latestOwnedApplication = [...(myApplications ?? [])].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  )[0];
  const applicationToShare = [
    ...(myApplications ?? []),
    ...pendingAppsForLandlord,
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
  const onboardingApplicationOnly =
    isSignupFlowRef.current && Boolean(latestOwnedApplication?._id);
  // When driven by an API request with an explicit application (or individual),
  // use exactly that primary option plus Create new – no extra cards.
  const hasApplicationOverride = requestOverride !== undefined;
  const displayedApplication = hasApplicationOverride
    ? (applicationOverride ?? null)
    : onboardingApplicationOnly
      ? latestOwnedApplication
      : applicationToShare;
  const hideIndividualOption = hasApplicationOverride
    ? Boolean(applicationOverride?._id)
    : false;

  const handleClose = () => {
    setStep("choice");
    setSelectedApp(null);
    setPendingVivia(null);
    onClose();
  };

  /* returns true if landlord requires consent the tenant hasn't given yet */
  const consentRequired = () => {
    if (!landlordData) return false;
    const insuranceEnabled = landlordData?.insurance_service_status;
    const affidavitEnabled = landlordData?.affidavit_activated;
    const hasInsuranceConsent =
      haveValue(user?.consents?.gdpr_consent) &&
      haveValue(user?.consents?.skor_consent);
    const hasAffidavitConsent = haveValue(user?.consents?.declaration_consent);
    return (
      (insuranceEnabled && !hasInsuranceConsent) ||
      (affidavitEnabled && !hasAffidavitConsent)
    );
  };

  /*
   * Gate every share action behind consent.
   * If consent is needed: close this modal, let parent show ConsentStepModal
   * with the captured action as the post-consent callback.
   * If consent is not needed: run action directly.
   */
  const withConsentGate = async (action) => {
    if (consentRequired()) {
      if (typeof onNeedsConsent !== "function") {
        toast.error(
          _t("consentRequired", "Consent is required before sharing")
        );
        return;
      }
      onNeedsConsent(landlordData, action);
      // API-driven queues keep this request active while the parent collects consent.
      if (!requestOverride) handleClose();
      return;
    }
    await action();
  };

  /* Vivia pre-share gate (onboarding landlord queue only). Runs BEFORE the
   * consent gate so the identification form opens while this modal is still
   * visible: consent may hide this modal via onNeedsConsent in the parent.
   * Cancel blocks the share and stays on the choice screen. */
  const isViviaGateActive = Boolean(enableViviaGate && requestOverride);

  const resolveFullApplication = (appOrId) => {
    const id = typeof appOrId === "string" ? appOrId : (appOrId?._id ?? null);
    if (!id) return typeof appOrId === "object" ? appOrId : null;
    const pool = [...(myApplications ?? []), ...(pendingApplications ?? [])];
    // applicationOverride in onboarding only carries _id/title/participants,
    // so look up the loaded application for the vivia flag.
    return (
      pool.find((a) => a?._id?.toString?.() === id?.toString?.()) ??
      (typeof appOrId === "object" ? appOrId : null)
    );
  };

  const getViviaRequirement = (applicationOrNull) => {
    const consentStatus = user?.consents?.client_identification_consent?.status;
    if (consentStatus === constants.ACCEPTED)
      return { required: false, reason: "consent-accepted" };
    const fullApp = applicationOrNull
      ? resolveFullApplication(applicationOrNull)
      : null;
    const appRequired = fullApp?.client_identification_required === true;
    const landlord = requestOverride?.landlord_id ?? {};
    const workspaceRef = requestOverride?.workspace_id;
    // Note: getLandlordRequestsByEmail populates landlord_id with name fields
    // only and leaves workspace_id unpopulated, so the landlord-side rule is
    // usually absent here. The tenant's own workspace carries the invite
    // workspace rule (set at signup), same source PostApplicationModals and
    // skorList use — that is the primary signal in this flow.
    const userWorkspaceRequired =
      user?.workspaceId?.client_identification_rule === true;
    const landlordRequired =
      requestOverride?.client_identification_rule === true ||
      landlord?.client_identification_rule === true ||
      (workspaceRef && typeof workspaceRef === "object"
        ? workspaceRef?.client_identification_rule === true
        : false) ||
      landlordData?.client_identification_rule === true ||
      userWorkspaceRequired;
    const required = appRequired || landlordRequired;
    return {
      required,
      reason: !required
        ? "not-required"
        : appRequired && landlordRequired
          ? "both"
          : appRequired
            ? "app-flag"
            : "landlord-rule",
      appRequired,
      landlordRequired,
      userWorkspaceRequired,
      appFlagUnknown: Boolean(applicationOrNull) && !fullApp,
    };
  };

  const withViviaGate = async (
    afterVivia,
    { application = null, appId = null, shareKind }
  ) => {
    if (!isViviaGateActive) {
      await afterVivia();
      return;
    }
    const reqId = requestId.current ?? null;
    const appRef = application ?? (appId ? { _id: appId } : null);
    const resolvedApp = appRef
      ? (resolveFullApplication(appRef) ?? appRef)
      : null;
    const check = getViviaRequirement(resolvedApp);
    console.log("[signup-flow]", "vivia check before share", {
      reqId,
      shareKind,
      appId: resolvedApp?._id ?? appId ?? null,
      appRequired: check.appRequired ?? false,
      landlordRequired: check.landlordRequired ?? false,
      userWorkspaceRequired: check.userWorkspaceRequired ?? false,
      appFlagUnknown: check.appFlagUnknown ?? false,
      consentStatus:
        user?.consents?.client_identification_consent?.status ?? null,
      required: check.required,
      reason: check.reason,
      overrideMode: Boolean(requestOverride),
    });
    if (!check.required) {
      console.log("[signup-flow]", "vivia not required, sharing", {
        reqId,
        shareKind,
        appId: resolvedApp?._id ?? appId ?? null,
      });
      await afterVivia();
      return;
    }
    // requestOverride.email is the tenant's own email (the request is keyed
    // by it), never the landlord's — so it must not be used as landlordEmail.
    const landlordEmail =
      landlordData?.email ?? requestOverride?.landlord_id?.email ?? null;
    const workspaceRef = requestOverride?.workspace_id;
    const workspace =
      workspaceRef && typeof workspaceRef === "object"
        ? workspaceRef
        : (user?.workspaceId ?? null);
    console.log(
      "[signup-flow]",
      "vivia required, opening identification form",
      {
        reqId,
        shareKind,
        appId: resolvedApp?._id ?? appId ?? null,
        reason: check.reason,
        landlordEmail,
      }
    );
    setProcessing(false);
    setPendingVivia({
      action: afterVivia,
      shareKind,
      appId: resolvedApp?._id ?? appId ?? null,
      landlordEmail,
      workspace,
    });
  };

  const handleViviaSubmitted = async () => {
    const deferred = pendingVivia?.action;
    const ctx = {
      reqId: requestId.current ?? null,
      shareKind: pendingVivia?.shareKind ?? null,
      appId: pendingVivia?.appId ?? null,
    };
    console.log(
      "[signup-flow]",
      "vivia identification submitted, proceeding to share",
      ctx
    );
    // Run the deferred share in this live instance. Never reload the user
    // here: a reload remounts the signup step and this continuation would run
    // in a dead closure (share succeeds server-side, UI state updates lost).
    // The parent reloads after advancing the queue (see handleLandlordShared).
    setPendingVivia(null);
    onViviaSubmitted?.();
    setProcessing(true);
    try {
      await deferred?.();
    } finally {
      setProcessing(false);
    }
  };

  /* Individual profile → respond to landlord request with optional skor_protect */
  const handleIndividual = async (skorProtect = true) => {
    if (!requestId.current) return;
    const reqId = requestId.current;
    console.log("[signup-flow]", "landlord share: individual selected", {
      reqId,
      overrideMode: Boolean(requestOverride),
    });
    const userId = user?._id ?? localStorage.getItem("saveduserid");
    const doShare = async () => {
      try {
        setProcessing(true);
        await acceptLandlordRequest(reqId, null, skorProtect);
        if (userId) await loadSharedProfiles(userId, "full");
        onDone();
      } catch {
        toast.error(_t("failedToShareProfile"));
      } finally {
        setProcessing(false);
      }
    };
    await withViviaGate(
      async () => {
        await withConsentGate(doShare);
      },
      { application: null, appId: null, shareKind: "individual" }
    );
  };

  const handleApplication = async (application) => {
    if (!requestId.current || !application?._id) return;
    const reqId = requestId.current;
    console.log("[signup-flow]", "landlord share: application selected", {
      reqId,
      appId: application._id,
      overrideMode: Boolean(requestOverride),
    });
    const userId = user?._id ?? localStorage.getItem("saveduserid");
    const doShare = async () => {
      try {
        setProcessing(true);
        await acceptLandlordRequest(reqId, application._id);
        if (userId) await loadSharedProfiles(userId, "full");
        onDone();
      } catch {
        toast.error(_t("failedToShareProfile"));
      } finally {
        setProcessing(false);
      }
    };
    await withViviaGate(
      async () => {
        await withConsentGate(doShare);
      },
      { application, appId: application._id, shareKind: "application" }
    );
  };

  /* Cross button on choice screen → share the displayed application */
  const handleChoiceClose = async () => {
    // Website entry (cases 3-4): closing shares nothing – the request stays
    // pending for the red dot in Share profile.
    console.log("[signup-flow]", "landlord share: X closed", {
      closeBehavior: closeBehavior ?? "share",
      reqId: requestId.current ?? null,
      displayedAppId: displayedApplication?._id ?? null,
    });
    if (closeBehavior === "nothing") {
      handleClose();
      return;
    }
    if (!requestId.current) {
      handleClose();
      return;
    }
    if (displayedApplication?._id) {
      await handleApplication(displayedApplication);
      return;
    }
    await handleIndividual();
  };

  /* Join existing group → consent to the application + respond to landlord request */
  const handleAcceptGroup = async () => {
    if (!selectedApp || !requestId.current) return;
    const appId = selectedApp._id;
    const reqId = requestId.current;
    console.log("[signup-flow]", "landlord share: join group selected", {
      reqId,
      appId,
      overrideMode: Boolean(requestOverride),
    });
    const doShare = async () => {
      try {
        setProcessing(true);
        const actions = [consentApplication(appId, "accepted")];
        actions.push(acceptLandlordRequest(reqId, appId));
        await Promise.all(actions);
        const userId = user?._id ?? localStorage.getItem("saveduserid");
        if (userId) await loadSharedProfiles(userId, "full");
        onDone();
      } catch {
        toast.error("Failed to join group");
      } finally {
        setProcessing(false);
      }
    };
    await withViviaGate(
      async () => {
        await withConsentGate(doShare);
      },
      { application: selectedApp, appId, shareKind: "join-group" }
    );
  };

  /* Decline → reject the application invitation */
  const handleDeclineGroup = async () => {
    if (!selectedApp) {
      setStep("choice");
      setSelectedApp(null);
      return;
    }
    const appId = selectedApp._id;
    try {
      setProcessing(true);
      await consentApplication(appId, "rejected");
      const userId = user?._id ?? localStorage.getItem("saveduserid");
      if (userId) await loadSharedProfiles(userId, "full");
    } catch {
      toast.error("Failed to decline");
    } finally {
      setProcessing(false);
    }
    setStep("choice");
    setSelectedApp(null);
  };

  /*
   * Create group → create application first, then respond to landlord request
   * with the new application ID.
   * Consent is checked before any action — participants list is captured in closure.
   * The Vivia check runs after creation with the new app id, before the share.
   */
  const handleCreateAndShare = async (participants, skorProtect = true) => {
    if (!requestId.current) return;
    const reqId = requestId.current;
    console.log("[signup-flow]", "landlord share: create group started", {
      reqId,
      overrideMode: Boolean(requestOverride),
    });
    const createThenShare = async () => {
      try {
        const res = await createApplication({
          participants,
          title: defaultTitle,
          skor_protect: skorProtect,
        });
        const appId = res?.success_res?._id ?? res?._id;
        console.log("[signup-flow]", "landlord share: group created", {
          reqId,
          appId: appId ?? null,
          overrideMode: Boolean(requestOverride),
        });
        const doShare = async () => {
          try {
            setProcessing(true);
            await acceptLandlordRequest(reqId, appId);
            const userId = user?._id ?? localStorage.getItem("saveduserid");
            await Promise.all([
              loadApplicationsData(),
              userId ? loadSharedProfiles(userId, "full") : Promise.resolve(),
            ]);
            onDone();
          } catch (err) {
            toast.error(
              err?.response?.data?.message ??
                _t("failedToCreateGroup", "Failed to create group")
            );
            throw err;
          } finally {
            setProcessing(false);
          }
        };
        await withViviaGate(doShare, { appId, shareKind: "created-group" });
      } catch (err) {
        toast.error(
          err?.response?.data?.message ??
            _t("failedToCreateGroup", "Failed to create group")
        );
        throw err;
      }
    };
    await withConsentGate(createThenShare);
  };

  if (!isOpen) return null;

  return (
    <div className="wrapper-modal isf-overlay">
      <div className="isf-container" onClick={(e) => e.stopPropagation()}>
        {/* While a share is in flight (e.g. after identification submit),
            hold a loader so the choice screen never sits interactive during
            the accept + reload + remount window. processing is always reset
            in a finally, and the consent-defer path never sets it. */}
        {landLordLoading || processing ? (
          <div className="isf-loading">
            <img
              src={SkorDefaultLoader}
              className="defaultSkorLoader"
              alt="Example"
            />
          </div>
        ) : step === "choice" ? (
          <ChoiceScreen
            landlordData={landlordData}
            applicationToShare={displayedApplication}
            applicationOnly={onboardingApplicationOnly}
            hideIndividualOption={hideIndividualOption}
            onSelectIndividual={async () => {
              if (user?.skor_protect) {
                setIndividualSharing(true);
                try {
                  await handleIndividual(true);
                } finally {
                  setIndividualSharing(false);
                }
                return;
              }
              setStep("individual");
            }}
            onSelectGroup={(app) => {
              setSelectedApp(app);
              setStep("join-group");
            }}
            onSelectApplication={(app) => {
              // API-driven mode: the application is already decided – share it.
              if (hasApplicationOverride) {
                handleApplication(app);
                return;
              }
              const isPending = pendingAppsForLandlord.some(
                (pendingApp) => pendingApp._id === app._id
              );
              if (isPending) {
                setSelectedApp(app);
                setStep("join-group");
                return;
              }
              handleApplication(app);
            }}
            onSelectCreate={() => {
              if (typeof onCreateNew === "function") {
                onCreateNew();
                return;
              }
              setStep("create-group");
            }}
            onClose={handleChoiceClose}
            _t={_t}
            user={user}
            processing={processing}
            individualSharing={individualSharing}
          />
        ) : step === "individual" ? (
          <IndividualScreen
            landlordData={landlordData}
            onBack={() => setStep("choice")}
            onConfirm={handleIndividual}
            processing={processing}
            _t={_t}
          />
        ) : step === "join-group" ? (
          <JoinGroupScreen
            app={selectedApp}
            landlordData={landlordData}
            onBack={() => setStep("choice")}
            onClose={handleClose}
            onAccept={handleAcceptGroup}
            onDecline={handleDeclineGroup}
            processing={processing}
            user={user}
            _t={_t}
          />
        ) : (
          <CreateGroupScreen
            onBack={() => setStep("choice")}
            onClose={handleClose}
            onCreateAndShare={handleCreateAndShare}
            user={user}
          />
        )}
        {/* No onClose in onboarding: the identification is mandatory before
            sharing, so the form renders without its top close buttons. */}
        {pendingVivia && (
          <ClientIdentificationForm
            user={user}
            workspace={pendingVivia.workspace}
            applicationId={pendingVivia.appId ?? null}
            landlordEmail={pendingVivia.landlordEmail ?? null}
            onSubmitted={handleViviaSubmitted}
          />
        )}
      </div>
    </div>
  );
};

export { isSharedWithLandlord, JoinGroupScreen };
export default InvitationShareModal;
